/**
 * Completeness math for the staff waste vendor data report. Pure: no database,
 * no clock reads — `now` is always injected so calendar boundaries are
 * testable. Kept in encamp-shared because both the API resolver and the CSV
 * export depend on it.
 */

import type { WasteVendorIntake } from "encamp-prisma/generated/client";

export type CadenceKind = "WEEKLY" | "MONTHLY" | "QUARTERLY" | "ANNUAL";

/**
 * Cadences whose periods are a whole number of calendar months. Weekly is the
 * exception — its periods are ISO weeks, so it takes a separate branch in the
 * period arithmetic rather than a months-per-period entry.
 */
type MonthlyCadence = Exclude<CadenceKind, "WEEKLY">;

export type WasteVendorDataStatus =
  | "INACTIVE"
  | "NEVER_RECEIVED"
  | "NO_CADENCE"
  | "CURRENT"
  | "DUE"
  | "OVERDUE";

export interface CompletenessInput {
  isActive: boolean;
  cadence: CadenceKind | null;
  /**
   * Not applicable to `WEEKLY`, whose deadline is the end of the following
   * week. The API rejects the combination so a value set here can never be
   * silently ignored.
   */
  dueByDayOfMonth: number | null;
  /** Latest shipped date across delivered data. Preferred coverage measure. */
  coveredThrough: Date | null;
  /**
   * Latest delivery receipt date. Doubles as the "has anything ever arrived"
   * signal, and as the coverage measure when `coveredThrough` is null (a
   * delivery whose summary carried no shipped range).
   */
  lastReceivedAt: Date | null;
  now: Date;
}

export interface CompletenessResult {
  status: WasteVendorDataStatus;
  periodsBehind: number;
}

const MONTHS_PER_PERIOD: Record<MonthlyCadence, number> = {
  MONTHLY: 1,
  QUARTERLY: 3,
  ANNUAL: 12,
};

/**
 * User-facing labels, defined once here so the Oak grid chips and the CSV
 * export cannot drift apart on wording — the same reason row assembly is
 * shared. `prettyPrintEnumValue` is deliberately not used: it would yield
 * "Never Received" and "Vendor Email" rather than the intended copy, and the
 * intake wording mirrors the CS team's existing tracker vocabulary.
 *
 * These strings are user-visible and the Oak smoke suite asserts on strings
 * like them — treat changes here as UI changes.
 */
export const STATUS_LABELS: Record<WasteVendorDataStatus, string> = {
  INACTIVE: "Inactive",
  NEVER_RECEIVED: "Never received",
  NO_CADENCE: "No cadence set",
  CURRENT: "Current",
  DUE: "Due",
  OVERDUE: "Overdue",
};

export const CADENCE_LABELS: Record<CadenceKind, string> = {
  WEEKLY: "Weekly",
  MONTHLY: "Monthly",
  QUARTERLY: "Quarterly",
  ANNUAL: "Annual",
};

export const INTAKE_LABELS: Record<WasteVendorIntake, string> = {
  VENDOR_EMAIL: "Vendor sends emails",
  SALESLOFT_OUTREACH: "SalesLoft outreach",
  BESPOKE_OUTREACH: "Bespoke outreach",
  PORTAL: "Portal",
  IMPLEMENTATION: "Implementation",
  BLOCKED: "Blocked",
};

/**
 * Collapses a vendor name, an observed `vendorSource` value, or a pipeline slug
 * to a comparable key. Lowercase and strip everything non-alphanumeric, so
 * "Safety Kleen", "Safety-Kleen", and "safety-kleen" all match without needing
 * an alias row.
 */
export function normalizeVendorKey(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, "");
}

const MS_PER_DAY = 86_400_000;
const DAYS_PER_WEEK = 7;
/**
 * The Unix epoch fell on a Thursday, so the Monday that opens its week is three
 * days earlier. Shifting day ordinals by this puts a week boundary at every
 * multiple of seven, making week index plain integer division.
 */
const EPOCH_WEEK_START_OFFSET = 3;

/** Months since epoch year 0, so period arithmetic is plain integer math. */
function monthOrdinal(date: Date): number {
  return date.getUTCFullYear() * 12 + date.getUTCMonth();
}

/** Whole days since the epoch, UTC. Time of day is discarded. */
function dayOrdinal(date: Date): number {
  const midnight = Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
  return Math.floor(midnight / MS_PER_DAY);
}

/** The day ordinal of the Monday that opens `weekIndex`. */
function weekStartOrdinal(weekIndex: number): number {
  return weekIndex * DAYS_PER_WEEK - EPOCH_WEEK_START_OFFSET;
}

/**
 * Which period a date falls in. For the month-based cadences, dividing the month
 * ordinal by the period length aligns quarters to Jan/Apr/Jul/Oct and annual
 * periods to January, which is what a calendar-quarter or calendar-year
 * deliverable means. Weekly divides day ordinals instead, giving ISO
 * Monday-to-Sunday weeks — a week is not a whole number of months, so it cannot
 * share the month path.
 */
function periodIndexOf(date: Date, cadence: CadenceKind): number {
  if (cadence === "WEEKLY") {
    return Math.floor((dayOrdinal(date) + EPOCH_WEEK_START_OFFSET) / DAYS_PER_WEEK);
  }
  return Math.floor(monthOrdinal(date) / MONTHS_PER_PERIOD[cadence]);
}

function lastDayOfMonth(year: number, month: number): number {
  // Day 0 of the next month is the last day of this one.
  return new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
}

/**
 * When the delivery for `periodIndex` is due: the configured day of the month
 * *following* the period's last month, clamped to that month's length. A null
 * due day means the last day of that month.
 *
 * Weekly has no configurable deadline — week W's data is due by the end of week
 * W+1, i.e. that week's Sunday. `dueByDayOfMonth` names a day of the *month*,
 * which cannot address a position within a week, so rather than reinterpret it
 * the API rejects it for weekly rows and this ignores it.
 */
function dueDateFor(
  periodIndex: number,
  cadence: CadenceKind,
  dueByDayOfMonth: number | null
): Date {
  if (cadence === "WEEKLY") {
    const sundayOfNextWeek = weekStartOrdinal(periodIndex + 1) + DAYS_PER_WEEK - 1;
    return new Date(sundayOfNextWeek * MS_PER_DAY);
  }

  const monthsPerPeriod = MONTHS_PER_PERIOD[cadence];
  const monthAfterPeriod = periodIndex * monthsPerPeriod + monthsPerPeriod;
  const year = Math.floor(monthAfterPeriod / 12);
  const month = monthAfterPeriod % 12;
  const lastDay = lastDayOfMonth(year, month);

  let day = lastDay;
  if (dueByDayOfMonth !== null) {
    day = Math.min(Math.max(dueByDayOfMonth, 1), lastDay);
  }

  return new Date(Date.UTC(year, month, day));
}

/**
 * The most recent period whose due date has already passed. The period
 * containing `now` cannot be due yet, so the answer is either the period just
 * before it or the one before that, depending on whether the due day has passed.
 */
function lastDuePeriodIndex(
  now: Date,
  cadence: CadenceKind,
  dueByDayOfMonth: number | null
): number {
  const currentPeriod = periodIndexOf(now, cadence);
  const mostRecentlyClosed = currentPeriod - 1;

  const dueDate = dueDateFor(mostRecentlyClosed, cadence, dueByDayOfMonth);
  // "Due by the 20th" gives the vendor all of the 20th to deliver, so the period
  // only goes overdue once that whole day has passed. `dueDateFor` returns UTC
  // midnight at the START of the due day, which would otherwise mark the period
  // overdue for the entire day it is actually still due.
  const deadline = Date.UTC(
    dueDate.getUTCFullYear(),
    dueDate.getUTCMonth(),
    dueDate.getUTCDate() + 1
  );

  if (deadline <= now.getTime()) {
    return mostRecentlyClosed;
  }
  return mostRecentlyClosed - 1;
}

export function computeCompleteness(input: CompletenessInput): CompletenessResult {
  const { isActive, cadence, dueByDayOfMonth, coveredThrough, lastReceivedAt, now } = input;

  if (!isActive) {
    return { status: "INACTIVE", periodsBehind: 0 };
  }
  if (lastReceivedAt === null && coveredThrough === null) {
    return { status: "NEVER_RECEIVED", periodsBehind: 0 };
  }
  if (cadence === null) {
    return { status: "NO_CADENCE", periodsBehind: 0 };
  }

  // Prefer the shipped range; a delivery with no shipped dates still proves
  // data arrived, so fall back to when it landed.
  //
  // `coveredThrough` is the only input here derived from vendor-supplied cell
  // values (the max parsed shipped date across a delivery's rows) and nothing
  // upstream bounds it, so one mistyped year would otherwise park a pair at
  // CURRENT indefinitely and hide every real outage behind it — the opposite of
  // what this report exists to do. A date past the end of today cannot describe
  // waste that has already shipped, so don't let it stand in for coverage; fall
  // back to when data actually landed, which is a server timestamp. The grace to
  // end-of-day absorbs a vendor in a later timezone reporting what is already
  // "tomorrow" in UTC, while still catching the typos, which miss by months.
  //
  // Only the status math distrusts the value. `coveredThrough` itself is left
  // alone so the grid's "Data through" column keeps showing it — staff need to
  // see the bad date to go fix it at the source.
  const endOfToday =
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()) + MS_PER_DAY - 1;
  const shippedCoverage =
    coveredThrough !== null && coveredThrough.getTime() > endOfToday ? null : coveredThrough;
  const coverageDate = shippedCoverage ?? lastReceivedAt;
  if (coverageDate === null) {
    return { status: "NEVER_RECEIVED", periodsBehind: 0 };
  }

  const coveredPeriod = periodIndexOf(coverageDate, cadence);
  const lastDuePeriod = lastDuePeriodIndex(now, cadence, dueByDayOfMonth);
  const periodsBehind = Math.max(0, lastDuePeriod - coveredPeriod);

  if (periodsBehind >= 1) {
    return { status: "OVERDUE", periodsBehind };
  }

  // Nothing is late. If the most recently closed period is still uncovered, a
  // delivery is expected soon — surface that as DUE rather than CURRENT.
  const mostRecentlyClosed = periodIndexOf(now, cadence) - 1;
  if (coveredPeriod < mostRecentlyClosed) {
    return { status: "DUE", periodsBehind: 0 };
  }

  return { status: "CURRENT", periodsBehind: 0 };
}
